import React from 'react';
import { WithdrawDetailsPopover } from '../../components/Popover';

interface IWithdrawTableData {
  transaction: string;
  transactionId: string;
  transactionType: string;
  transactionDate: string;
  fee: string;
  amount: string;
  status: string;
}

interface WithdrawTableProps {
  withdrawHeaders: string[];
  withdrawTableData: IWithdrawTableData[];
  onTransactionClick: (transactionId: string) => void;
  popoverVisible: boolean;
  popoverTransactionId: string | null;
  closePopover: () => void;
}

const WithdrawTable: React.FC<WithdrawTableProps> = ({
  withdrawHeaders,
  withdrawTableData,
  onTransactionClick,
  popoverVisible,
  popoverTransactionId,
  closePopover,
}) => {
  return (
    <section className="w-full">
      {/* Desktop/tablet table view - hidden on small screens */}
      <div className="hidden sm:block overflow-x-auto">
        <div className="min-w-[700px] grid grid-cols-7 gap-0 bg-slate-50 w-full text-sm font-semibold text-slate-900 whitespace-nowrap">
          {withdrawHeaders.map((header, index) => (
            <div
              key={index}
              className="p-3 sm:p-4 cursor-pointer flex justify-between items-center bg-slate-50 text-xs sm:text-sm"
            >
              {header}
            </div>
          ))}
        </div>

        <section className="w-full min-w-[700px]">
          {withdrawTableData.map((row, index) => (
            <section key={index}>
              <section className="grid grid-cols-7 gap-0 bg-white w-full text-xs sm:text-sm whitespace-nowrap border-b">
                <div className="flex items-center mx-2 sm:mx-4 py-3 sm:py-4 overflow-hidden text-ellipsis">
                  {row.transaction}
                </div>
                <div
                  className="flex items-center mx-2 sm:mx-4 py-3 sm:py-4 overflow-hidden text-ellipsis underline cursor-pointer"
                  onClick={() => onTransactionClick(row.transactionId)}
                >
                  {row.transactionId}
                </div>
                <div className="flex items-center mx-2 sm:mx-4 py-3 sm:py-4 overflow-hidden text-ellipsis">
                  {row.transactionType}
                </div>
                <div className="flex items-center mx-2 sm:mx-4 py-3 sm:py-4 overflow-hidden text-ellipsis">
                  {row.transactionDate}
                </div>
                <div className="flex items-center mx-2 sm:mx-4 py-3 sm:py-4 overflow-hidden text-ellipsis">
                  {row.fee}
                </div>
                <div className="flex items-center mx-2 sm:mx-4 py-3 sm:py-4 overflow-hidden text-ellipsis">
                  {row.amount}
                </div>
                <div className="flex items-center mx-2 sm:mx-4 py-3 sm:py-4 overflow-hidden text-ellipsis">
                  {row.status}
                </div>
              </section>

              {popoverVisible && popoverTransactionId === row.transactionId && (
                <WithdrawDetailsPopover
                  transactionId={row.transactionId}
                  asset="USD"
                  amount={row.amount}
                  fee={row.fee}
                  total={`$ ${parseFloat(row.amount.replace('$', '')) + parseFloat(row.fee.replace('$', ''))}`}
                  date={row.transactionDate}
                  onClose={closePopover}
                />
              )}
            </section>
          ))}
        </section>
      </div>

      {/* Mobile card view - only visible on small screens */}
      <div className="sm:hidden w-full">
        {withdrawTableData.map((row, index) => (
          <div 
            key={index} 
            className="mb-4 bg-white rounded-md border border-slate-200 overflow-hidden"
          >
            <div className="p-3 border-b border-slate-200">
              <div className="flex justify-between items-center">
                <div className="text-sm font-medium">{row.transaction}</div>
                <div className="text-xs px-2 py-1 rounded-full bg-slate-100">
                  {row.status}
                </div>
              </div>
              <div 
                className="text-xs text-blue-600 underline mt-1 cursor-pointer"
                onClick={() => onTransactionClick(row.transactionId)}
              >
                ID: {row.transactionId}
              </div>
            </div>
            
            <div className="p-3 space-y-2">
              <div className="flex justify-between">
                <span className="text-xs text-slate-500">Type:</span>
                <span className="text-sm">{row.transactionType}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs text-slate-500">Date:</span>
                <span className="text-sm">{row.transactionDate}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs text-slate-500">Fee:</span>
                <span className="text-sm font-medium">{row.fee}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs text-slate-500">Amount:</span>
                <span className="text-sm font-medium">{row.amount}</span>
              </div>
              <div className="flex justify-between border-t border-slate-100 pt-2">
                <span className="text-xs text-slate-500">Total:</span>
                <span className="text-sm font-bold">
                  $ {parseFloat(row.amount.replace('$', '')) + parseFloat(row.fee.replace('$', ''))}
                </span>
              </div>
            </div>

            {popoverVisible && popoverTransactionId === row.transactionId && (
              <WithdrawDetailsPopover
                transactionId={row.transactionId}
                asset="USD"
                amount={row.amount}
                fee={row.fee}
                total={`$ ${parseFloat(row.amount.replace('$', '')) + parseFloat(row.fee.replace('$', ''))}`}
                date={row.transactionDate}
                onClose={closePopover}
              />
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

export default WithdrawTable;
